/**
 * Admin Observer Phone Management
 * - Add/Edit observer phone numbers (agent number + virtual number)
 * - View all observers with calling status
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Phone, User, Plus, Edit, Trash2, Check, X, 
  RefreshCw, Loader2, PhoneCall, AlertCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminObserverPhones = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [observers, setObservers] = useState([]);
  const [summary, setSummary] = useState({});
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    observer_id: '',
    agent_number: '',
    virtual_number: '',
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const getToken = () => localStorage.getItem('admin_token') || 'admin';

  const loadObservers = async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/admin/observers-calling-status?token=${token}`);
      setObservers(response.data.observers || []);
      setSummary(response.data.summary || {});
    } catch (err) {
      console.error('Error loading observers:', err);
      toast({ title: "Error", description: "Failed to load observers", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadObservers();
  }, []);

  const openAddForm = (observer = null) => {
    if (observer) {
      setEditingId(observer.id);
      setFormData({
        observer_id: observer.id,
        agent_number: observer.agent_number || '',
        virtual_number: observer.virtual_number || '',
        is_active: observer.calling_enabled !== false
      });
    } else {
      setEditingId(null);
      setFormData({
        observer_id: '',
        agent_number: '',
        virtual_number: '',
        is_active: true
      });
    }
    setShowForm(true);
  };

  const saveConfig = async () => {
    if (!formData.observer_id || !formData.agent_number) {
      toast({ title: "Error", description: "Please select observer and enter agent number", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const token = getToken();
      await axios.post(`${API_URL}/api/admin/observer-phones?token=${token}`, formData);
      
      toast({ title: "Success", description: "Phone configuration saved" });
      setShowForm(false);
      setFormData({ observer_id: '', agent_number: '', virtual_number: '', is_active: true });
      loadObservers();
    } catch (err) {
      toast({ 
        title: "Error", 
        description: err.response?.data?.detail || "Failed to save configuration",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async (observerId) => {
    if (!window.confirm('Are you sure you want to remove phone configuration for this observer?')) return;

    try {
      const token = getToken();
      await axios.delete(`${API_URL}/api/admin/observer-phones/${observerId}?token=${token}`);
      toast({ title: "Success", description: "Configuration removed" });
      loadObservers();
    } catch (err) {
      toast({ title: "Error", description: "Failed to remove configuration", variant: "destructive" });
    }
  };

  const testCall = async (observer) => {
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/calling/test?destination_number=${observer.agent_number}&token=${token}`
      );
      
      if (response.data.success) {
        toast({ title: "Test Call Initiated", description: `Calling ${observer.agent_number}...` });
      } else {
        toast({ title: "Call Failed", description: response.data.message, variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to initiate test call", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Observer Phone Management</h2>
          <p className="text-sm text-slate-500">Configure calling numbers for observers</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadObservers} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={() => openAddForm()} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-1" />
            Add Configuration
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-slate-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-700">{summary.total || 0}</p>
            <p className="text-sm text-slate-500">Total Observers</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-700">{summary.configured || 0}</p>
            <p className="text-sm text-green-600">Phone Configured</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-700">{summary.not_configured || 0}</p>
            <p className="text-sm text-yellow-600">Not Configured</p>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="border-2 border-green-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" />
              {editingId ? 'Edit Phone Configuration' : 'Add Phone Configuration'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Observer *</Label>
                <select
                  value={formData.observer_id}
                  onChange={(e) => setFormData({ ...formData, observer_id: e.target.value })}
                  className="mt-1 w-full h-10 px-3 border rounded-md"
                  disabled={!!editingId}
                >
                  <option value="">Select Observer</option>
                  {observers.filter(o => !o.phone_configured || o.id === editingId).map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Agent Number * (Observer's Phone)</Label>
                <Input
                  placeholder="e.g., 8291188188"
                  value={formData.agent_number}
                  onChange={(e) => setFormData({ ...formData, agent_number: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">This phone will ring when calls are triggered</p>
              </div>
              
              <div>
                <Label>Virtual Number (Caller ID)</Label>
                <Input
                  placeholder="Leave blank for default"
                  value={formData.virtual_number}
                  onChange={(e) => setFormData({ ...formData, virtual_number: e.target.value })}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Number shown to parents (optional)</p>
              </div>
              
              <div className="flex items-center gap-2 mt-6">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <Label htmlFor="is_active">Enable Calling</Label>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={saveConfig} disabled={saving} className="bg-green-600 hover:bg-green-700">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Save Configuration
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Observers Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Observers</CardTitle>
          <CardDescription>Click on an observer to configure their phone</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Observer</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Agent Number</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Virtual Number</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {observers.map((observer) => (
                    <tr key={observer.id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="font-medium">{observer.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        {observer.agent_number ? (
                          <span className="font-mono">{observer.agent_number}</span>
                        ) : (
                          <span className="text-slate-400">Not set</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        {observer.virtual_number ? (
                          <span className="font-mono text-sm">{observer.virtual_number}</span>
                        ) : (
                          <span className="text-slate-400">Default</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        {observer.phone_configured ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            Configured
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                            Not Configured
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAddForm(observer)}
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {observer.phone_configured && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => testCall(observer)}
                                title="Test Call"
                                className="text-green-600"
                              >
                                <PhoneCall className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => deleteConfig(observer.id)}
                                title="Remove"
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminObserverPhones;
